import type { BasketItem, CreateBasketItem, UpdateBasketItem } from '@accesso/cc-ecomm-api';
import type { KeyValueDto } from '@accesso/cc-ecomm-api/lib/src/core/json-api/model/standard';
import type { Observable } from 'rxjs';
import { isPlatformBrowser, PlatformLocation } from '@angular/common';
import { computed, effect, inject, Injector, PLATFORM_ID, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { cloneDeep } from 'lodash-es';
import { catchError, filter, first, map, of, switchMap, throwError } from 'rxjs';

import { EventName, EventService, isNonEmptyString } from '@accesso/cc-std-lib';
import { BasketModel } from '@accesso/cc-ui/model';
import { JsonApiService } from '@accesso/ng-json-api';

import { QUERY_PARAM_CART_ID } from '../../core/enums/query-params';
import { StorageKey } from '../storage/storage.keys';
import { StorageService } from '../storage/storage.token';

interface CartState {
	cartId: string | null;
	basket: BasketModel | null;
	creating: boolean;
	created: boolean;
	errorCreating: boolean;
}

interface ProductCartItem {
	itemId: string;
	productId: string;
	customerTypeId: number | string;
}

const initialState: CartState = {
	cartId: null,
	basket: null,
	creating: false,
	created: false,
	errorCreating: false,
};

const UUID_KEY = `cc_fe_uuid`;

export const CartStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(({ basket }) => ({
		itemCount: computed(() => basket()?.totalItems() ?? 0),
		/** True while the basket has an update (e.g. a removal) in flight to the backend. */
		updating: computed(() => basket()?.state().updating ?? false),
		/** True while the basket is loading. */
		loading: computed(() => basket()?.state().loading ?? false),
	})),
	withMethods(
		(store, injector = inject(Injector), event = inject(EventService), jsonApi = inject(JsonApiService), storageService = inject(StorageService)) => ({
			/**
			 * Creates a basket. If we're unable to create a basket it will return null.
			 */
			clearCart(): void {
				storageService.remove(StorageKey.CART_ID);
				patchState(store, { ...initialState });
			},
			/**
			 * Creates a basket. If we're unable to create a basket it will return null.
			 */
			createBasket(): Observable<BasketModel | null> {
				patchState(store, { creating: true, errorCreating: false });

				return jsonApi.createRecord(BasketModel, { data: { type: 'basket' } }).pipe(
					first(),
					map((basket) => {
						if (basket) {
							storageService.add(StorageKey.CART_ID, basket.id);
							patchState(store, { basket, creating: false, created: true, errorCreating: false, cartId: basket.id });
						} else {
							patchState(store, { basket, creating: false, created: false, errorCreating: true });
						}

						return basket;
					}),
					catchError((error) => {
						patchState(store, { creating: false, created: false, errorCreating: true });
						return throwError(() => error);
					})
				);
			},
			/**
			 * Set a cart id and trigger a load
			 * @param cartId The cart identifier
			 */
			setCartId(cartId: string): void {
				const basket = jsonApi.findOne(BasketModel, cartId);

				patchState(store, { cartId, basket });

				const effectRef = effect(
					() => {
						const state = basket.state();
						if (state.loading) return;
						if (state.loaded) {
							// If we loaded a cart through a query param and it loaded, store it in local storage
							storageService.add(StorageKey.CART_ID, cartId);
							return effectRef.destroy();
						}

						const { errors } = state;
						if (errors?.[0]?.status === '404') {
							untracked(() => {
								patchState(store, { cartId: null, basket: null });
								storageService.remove(StorageKey.CART_ID);
							});
						}

						effectRef.destroy();
					},
					{ injector, manualCleanup: true }
				);
			},
			/**
			 * Removes a single item from the basket
			 * @param item The item to remove
			 */
			removeItem(item: BasketItem): void {
				this.removeItems([item]);
			},

			/**
			 * Removes multiple items from the basket.
			 *
			 * Removal is sent to the backend as a full replacement of the basket's item
			 * array, built from the current client-side snapshot. To avoid sending a stale
			 * payload that still references an already-removed item (CM-9332), a removal is
			 * ignored while the basket is already updating or loading, so the next removal
			 * only builds its payload once the basket state has settled.
			 * @param items The items to remove
			 */
			removeItems(items: BasketItem[]): void {
				const currentBasket = store.basket();
				if (!currentBasket) return;

				// Ignore a removal while the basket is mid-update or loading so we
				// never build a removal payload from a stale snapshot.
				const { updating, loading } = currentBasket.state();
				if (updating || loading) return;

				const ids: string[] = [];
				const itemsToRemove: ProductCartItem[] = [];

				items.forEach((item) => {
					const { id: itemId, productId, customerTypeId } = item;

					ids.push(itemId);

					itemsToRemove.push({
						itemId,
						productId: productId.toString(),
						customerTypeId,
					});
				});

				event.publish({
					event: EventName.PRODUCT_REMOVE_FROM_CART,
					details: {
						items: itemsToRemove,
					},
				});

				currentBasket.removeItems(ids);
			},

			/**
			 * Updates a single item's quantity
			 * @param item The item to update
			 * @param quantity The new quantity
			 * @param attributes The new attributes to replace the existing ones
			 * @returns
			 */
			updateItem(item: BasketItem, quantity: number, attributes?: KeyValueDto[]): void {
				const currentBasket = store.basket();

				if (!currentBasket) return;

				if (quantity <= 0) {
					this.removeItem(item);
				} else {
					const items = cloneDeep(currentBasket.items());

					const itemIndex = items.findIndex((i) => i.id === item.id);
					const updateItem = items[itemIndex];
					if (itemIndex > -1 && updateItem) {
						updateItem.quantity = quantity;
						if (attributes?.length) {
							updateItem.attributes = attributes;
						}

						currentBasket.replaceItems(items);
					}
				}
			},

			/**
			 * Adds new items to the basket
			 * @param items The items to add
			 */
			addItems(items: CreateBasketItem[]): Observable<BasketModel> {
				const newItems: string[] = [];

				items.forEach((item) => {
					if (!Array.isArray(item.attributes)) {
						item.attributes = [];
					}

					if (!item.attributes.some((attr) => attr.key === UUID_KEY)) {
						const value = crypto.randomUUID();
						item.attributes.push({ key: UUID_KEY, value });
						newItems.push(value);
					}
				});

				const currentBasket = store.basket();
				if (currentBasket) {
					const existingItems = currentBasket.items().map(
						({
							id,
							media,
							quantity,
							productId,
							customerTypeId,
							externalProductId,
							attributes,
							startDateTime,
							endDateTime,
							person,
						}): UpdateBasketItem => ({
							id,
							media,
							quantity,
							externalProductId,
							productId,
							customerTypeId,
							startDateTime,
							endDateTime,
							attributes,
							person,
						})
					);

					currentBasket.replaceItems([...existingItems, ...items]);

					return toObservable(currentBasket.state, { injector }).pipe(
						filter((state) => !state.loading && !state.updating),
						switchMap((state) => {
							if (!state.loaded) throw new Error('Basket failed to load');
							if (state.updatingError) {
								if (state.updatingErrors?.find((error) => error.detail?.includes('PURCHASED status and cannot'))) {
									storageService.remove(StorageKey.CART_ID);
									patchState(store, { ...initialState });
									return this.addItems(items);
								}
								throw new Error(`Unable to add to basket: ${JSON.stringify(state.updatingErrors)}`);
							}
							this.publishNewItems(newItems);
							return of(currentBasket);
						})
					);
				}

				return this.createBasket().pipe(
					switchMap((newBasket: BasketModel | null) => {
						if (!newBasket) {
							throw new Error('Failed to create basket');
						}

						newBasket.replaceItems(items);

						return toObservable(newBasket.state, { injector }).pipe(
							filter((state) => !state.loading && !state.updating),
							map((state) => {
								if (state.updatingError) throw new Error(`Unable to add to basket: ${JSON.stringify(state.updatingErrors)}`);
								this.publishNewItems(newItems);
								return newBasket;
							})
						);
					})
				);
			},
			publishNewItems(newItemUuids: string[]): void {
				const basket = store.basket();
				if (!basket) return;

				const basketItems = basket.items() ?? [];

				if (!newItemUuids.length) return;

				event.publish({
					event: EventName.PRODUCT_ADD_TO_CART,
					details: {
						items: newItemUuids.reduce((acc: ProductCartItem[], uuid) => {
							const foundItem = basketItems.find((basketItem) => basketItem.attributes?.find((attr) => attr.key === UUID_KEY)?.value === uuid);

							if (foundItem) {
								acc.push({
									itemId: foundItem.id,
									productId: foundItem.productId.toString(),
									customerTypeId: foundItem.customerTypeId.toString(),
								});
							}

							return acc;
						}, []),
					},
				});
			},
		})
	),
	withHooks((store) => ({
		/**
		 * Initializes the cart. This should be called at most once and in the root component.
		 */
		onInit(): void {
			const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
			const storageService = inject(StorageService);
			const queryParams = new URLSearchParams(inject(PlatformLocation).search);
			const queryParamCartId = isBrowser ? queryParams.get(QUERY_PARAM_CART_ID) : null;
			const cartId = queryParamCartId ?? storageService.get(StorageKey.CART_ID);

			if (isNonEmptyString(cartId)) {
				store.setCartId(cartId);
			}
		},
	}))
);
